<script setup lang="ts">
import { Dialog } from "../../../../base-components/Headless";
import {
  FormLabel,
  FormInput,
  FormSwitch
} from "../../../../base-components/Form";
import Button from "../../../../base-components/Button";
import LoadingIcon from "../../../../base-components/LoadingIcon";
import Alert from "../../../../base-components/Alert/Alert.vue";
import Lucide from "../../../../base-components/Lucide/Lucide.vue";
import { reactive, ref, toRefs } from "vue";
import { helpers, maxLength, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { useMensajesStore } from "../../../../services/mensajes";
import { useProductosCategoriasStore } from "../../../../services/productosCategorias";
import type { CategoriaProductos } from "@fixeduy/restaurantestypes";
import { utilFechaGetCurrentTime } from "../../../../utils/fecha";

interface Props {
  categoria: CategoriaProductos;
}

const props = defineProps<Props>();
const emit = defineEmits<{ (e: "cerrar"): void }>();

const mensajesStore = useMensajesStore();
const categoriasStore = useProductosCategoriasStore();

const cargando = ref(false);

const formData = reactive({
  codigo: props.categoria.codigo ?? "",
  nombre: props.categoria.nombre ?? "",
  activo: props.categoria.activo ?? true
});

const rules = {
  codigo: {},
  nombre: {
    required: helpers.withMessage(
      "El campo nombre no puede estar vacío",
      required
    ),
    maxLength: helpers.withMessage(
      "El nombre no puede superar los 80 caracteres",
      maxLength(80)
    )
  }
};

const validate = useVuelidate(rules, toRefs(formData));

const handlerEditar = async () => {
  const result = await validate.value.$validate();
  if (!result) {
    mensajesStore.mostrarModal(
      "Error",
      "Hay errores en el formulario. Verificalos e intentá de nuevo.",
      mensajesStore.mensajeType.error
    );
    return;
  }

  cargando.value = true;
  try {
    const categoria: CategoriaProductos = {
      id: props.categoria.id,
      version: 1,
      codigo: formData.codigo,
      nombre: formData.nombre,
      activo: formData.activo,
      ultimaEdicion: utilFechaGetCurrentTime()
    };
    const ok = await categoriasStore.updateCategoria(categoria);

    if (ok) {
      mensajesStore.mostrarToast(
        "Categoría actualizada",
        `Se actualizó la categoría "${formData.nombre}".`,
        mensajesStore.mensajeType.exito
      );
      emit("cerrar");
    } else {
      mensajesStore.mostrarModal(
        "Error",
        "No se pudo actualizar la categoría. Intentá de nuevo.",
        mensajesStore.mensajeType.error
      );
    }
  } finally {
    cargando.value = false;
  }
};
</script>

<template>
  <Dialog :open="true" @close="() => emit('cerrar')" size="xl">
    <Dialog.Panel>
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">
          Editar categoría: {{ categoria.nombre }}
        </h2>
      </Dialog.Title>

      <Dialog.Description>
        <!-- Código -->
        <!-- <div class="flex mt-3">
          <div class="flex-none text-right pr-5 mt-2">
            <FormLabel class="w-20 md:w-32 sw:w-20">Código</FormLabel>
          </div>
          <div class="flex-1">
            <FormInput
              type="text"
              v-model.trim="validate.codigo.$model"
              :class="{
                'border-danger': validate.codigo.$error
              }"
              placeholder="Código"
            />
          </div>
        </div> -->

        <!-- Nombre -->
        <div class="flex mt-5">
          <div class="flex-none text-right pr-5 mt-2">
            <FormLabel class="w-20 md:w-32 sw:w-20">Nombre</FormLabel>
          </div>
          <div class="flex-1">
            <FormInput
              type="text"
              v-model.trim="validate.nombre.$model"
              :class="{
                'border-danger': validate.nombre.$error
              }"
              placeholder="Nombre"
            />
          </div>
        </div>

        <!-- Activo -->
        <div class="flex mt-5 items-center">
          <div class="flex-none text-right pr-5 mt-2">
            <FormLabel class="w-20 md:w-32 sw:w-20">Activo</FormLabel>
          </div>
          <div class="flex-1">
            <FormSwitch class="flex items-center">
              <FormSwitch.Input
                type="checkbox"
                id="rubro-activo"
                v-model="formData.activo"
              />
              <FormSwitch.Label class="ml-2">
                {{ formData.activo ? "Activo" : "Inactivo" }}
              </FormSwitch.Label>
            </FormSwitch>
          </div>
        </div>

        <!-- Validación final -->
        <Alert
          variant="outline-danger"
          class="flex items-center my-4"
          v-if="validate?.$error"
        >
          <Lucide icon="AlertCircle" class="w-6 h-6 mr-2" />
          <ul>
            <li v-for="e in validate.$errors" :key="e.$uid">
              {{ e.$message }}
            </li>
          </ul>
        </Alert>
      </Dialog.Description>

      <Dialog.Footer>
        <div class="flex text-right" v-if="!cargando">
          <div class="flex-1 text-left">
            <Button
              variant="outline-secondary"
              :disabled="cargando"
              @click="() => emit('cerrar')"
            >
              Cerrar
            </Button>
          </div>
          <div class="flex-1">
            <Button
              variant="primary"
              :disabled="cargando"
              @click="handlerEditar"
            >
              Guardar
            </Button>
          </div>
        </div>
        <div v-else class="flex my-6">
          <div class="mx-auto">
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
